/**
 * Bulk QR ZIP aggregation.
 * Consumes renderQRToPNG + buildRoomFilename from qr.ts and produces a
 * single Blob (application/zip) containing one PNG per room.
 *
 * Filename rules, collision handling, and slug rules are documented in
 * qr.ts. The zipName option is accepted for API symmetry with the dialog
 * but is applied at download click time (<a download={zipName}>), not
 * stored inside the ZIP.
 */
import JSZip from 'jszip'
import { buildRoomFilename, renderQRToPNG } from './qr'
import type { Building, Room } from '@/types'

export interface BuildQRZipOpts {
  labeled: boolean
  /** ZIP filename passed to the download anchor by the caller. */
  zipName: string
  /** Base URL used to build per-room public URLs, e.g. `window.location.origin`. */
  appBaseUrl: string
}

/**
 * Populates a JSZip instance with one PNG per room.
 * Extracted so unit tests can assert ZIP contents via `generateAsync`
 * without round-tripping through a Blob (jsdom's Blob lacks
 * `arrayBuffer()` so loading a generated blob back through JSZip in a
 * test environment is flaky).
 */
export async function populateZip(
  rooms: Room[],
  buildings: Building[],
  opts: BuildQRZipOpts
): Promise<JSZip> {
  const buildingById = new Map(buildings.map((b) => [b.id, b]))
  const zip = new JSZip()
  const collisionCounts = new Map<string, number>()

  for (const room of rooms) {
    const building = buildingById.get(room.building_id)
    if (!building) {
      console.warn(
        `[buildQRZip] skipping room ${room.id} (${room.name}): building ${room.building_id} not in supplied list`
      )
      continue
    }

    const url = `${opts.appBaseUrl}/room/${room.id}`
    const png = await renderQRToPNG(url, {
      labeled: opts.labeled,
      roomName: room.name,
      buildingName: building.name,
    })

    const collisionKey = buildRoomFilename(room, building, 0)
    const count = collisionCounts.get(collisionKey) ?? 0
    collisionCounts.set(collisionKey, count + 1)
    const filename = buildRoomFilename(room, building, count)

    zip.file(filename, png)
  }

  return zip
}

/**
 * Builds a ZIP blob containing one QR PNG per room.
 * Rooms whose `building_id` does not appear in the `buildings` array are
 * skipped with a console.warn (defensive — the caller should have filtered
 * already, but cross-org stragglers shouldn't crash the download).
 */
export async function buildQRZip(
  rooms: Room[],
  buildings: Building[],
  opts: BuildQRZipOpts
): Promise<Blob> {
  const zip = await populateZip(rooms, buildings, opts)
  return zip.generateAsync({ type: 'blob' })
}
